import { useContext, useEffect, useRef, useState } from "react"
import { componentLoadingActive, ComponentLoadingScreen } from "./Background"
import { FaInfo } from "react-icons/fa"
import { AccountContext } from "../contexts/AccountContexts"
import { PermissionContext } from "../contexts/PermissionContexts"
import { useTranslation } from "react-i18next"
import { NotificationContext } from "../contexts/NotificationContexts"
import { requestAnnouncements, requestEmployeeAnnouncements, requestFetchEmployeeAnnouncementReads } from "../requests/setup_requests"


export const AnnouncementsComponent = ({ fetchCall, isEmployee = false, showNoAnnouncementsMessage=false }) => {
    const { t } = useTranslation('dashboard')
    const { language, user } = useContext(AccountContext)
    const { permissions, hasPermission } = useContext(PermissionContext)
    const { socket, setPlayerAnnouncements, setEmployeeAnnouncements } = useContext(NotificationContext)

    const [announcements, setAnnouncements] = useState([])
    const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0)
    const [animStart, setAnimStart] = useState(false)
    const [timerStop, setTimerStop] = useState(false)
    const [touchStart, setTouchStart] = useState(null)
    const [touchEnd, setTouchEnd] = useState(null)
    const [readAnnouncements, setReadAnnouncements] = useState([])

    const announcementRef = useRef(null)
    const minSwipeDistance = 50

    useEffect(() => {
        if (permissions.length > 0 && hasPermission("announcement:view")) {
            componentLoadingActive(true, "announcements-panel")
            fetchCall({hasPermission: false}).then((data) => {
                setAnnouncements(data.announcements)
                if(fetchCall === requestAnnouncements){
                    setPlayerAnnouncements(data.announcements)
                }else if (fetchCall === requestEmployeeAnnouncements){
                    setEmployeeAnnouncements(data.announcements)
                }
                componentLoadingActive(false, "announcements-panel")
            })
            if(isEmployee){
                requestFetchEmployeeAnnouncementReads({}).then((data) => {
                    setReadAnnouncements(data.announcements)
                })   
            }
        }
    }, [permissions, socket])


    const handleSelectAnnouncement = (index) => {
        setCurrentAnnouncementIndex(index)
        const selected = announcements[index];

        if (!selected.read) {
            socket.emit("announcement_read", { announcement_id: selected.id });
            requestFetchEmployeeAnnouncementReads({}).then((data) => {
                setReadAnnouncements(data.announcements)
            })
        }
        socket.off("announcement_read")
    }


    useEffect(() => {
        let interval;
        if (announcements.length > 1 && !timerStop) {

            interval = setInterval(() => {
                setAnimStart(true)
                setTimeout(() => {
                    setCurrentAnnouncementIndex((prev) => (prev + 1) % announcements.length)
                    setAnimStart(false)
                }, 500)
            }, 5000);

            return () => clearInterval(interval);
        }
        else if (timerStop) {
            clearInterval(interval)
        }
    }, [timerStop, announcements]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (announcementRef.current && announcementRef.current.contains(event.target)) {
                setTimerStop(true);
            }
            else if (announcementRef.current && !announcementRef.current.contains(event.target)) {
                setTimerStop(false);
            }
        };

        document.addEventListener('mouseover', handleClickOutside);

        return () => {
            document.removeEventListener('mouseover', handleClickOutside);
        };
    }, []);


    const onTouchStart = (e) => {
        setTouchEnd(null)
        setTouchStart(e.targetTouches[0].clientX)
    }

    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX)

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return
        const distance = touchStart - touchEnd
        const isLeftSwipe = distance > minSwipeDistance
        const isRightSwipe = distance < -minSwipeDistance

        if (isLeftSwipe) {
            setAnimStart(true)
            setTimerStop(true);
            setTimeout(() => {
                setCurrentAnnouncementIndex((prev) => (prev + 1) % announcements.length)
                setAnimStart(false)
            }, 500)
        }
        else if (isRightSwipe) {
            setAnimStart(true)
            setTimerStop(true);
            setTimeout(() => {
                setCurrentAnnouncementIndex((prev) => (prev - 1 + announcements.length) % announcements.length)
                setAnimStart(false)
            }, 500)
        }

    }

    const isMoreThanOneDayPassed = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();

        const diffMs = today - date;
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        return diffDays > 1;
    }

    let announcementTitle = ""
    let announcementMessage = ""

    if (announcements.length !== 0) {
        if (announcements[currentAnnouncementIndex].engTitle && language !== "tr") {
            announcementTitle = announcements[currentAnnouncementIndex].engTitle
            announcementMessage = announcements[currentAnnouncementIndex].engMessage
        }
        else {
            announcementTitle = announcements[currentAnnouncementIndex].title
            announcementMessage = announcements[currentAnnouncementIndex].message
        }
    }

    if (!hasPermission("announcement:view")) {
        return <></>
    }

    return (
        <div className="w-full max-w-[1200px]" ref={announcementRef}>

            <div dir="ltr" className={
                `flex ${announcements.length === 0 && "hidden"} 
                ${isEmployee ?
                    readAnnouncements?.some(
                        (a) =>
                        a.announcement_id === announcements[currentAnnouncementIndex]?.id &&
                        a.user_id === user.id
                    )
                    ? " border-primary "
                    : " border-yellow-500 "
                :
                    !isEmployee && isMoreThanOneDayPassed(
                        announcements[currentAnnouncementIndex]?.startDate
                    )
                    ? " border-primary "
                    : " border-yellow-500 "
                } w-full max-w-[1200px] h-40 overflow-clip hover:h-40 max-h-max hover:overflow-auto bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 p-2 rounded-lg border-2 transition-all duration-200 relative`}
                onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
            >
                <ComponentLoadingScreen tag={"announcements-panel"} />
                <div className="flex gap-2">
                    <div className="flex flex-col items-center mt-5 mx-2">
                        <div className="flex flex-col items-center justify-center border-2 border-primary text-primary dark:border-blue-500 dark:text-blue-500 rounded-lg p-1">
                            <FaInfo size={32} />
                        </div>
                    </div>
                    <div className={`w-full flex flex-col ${animStart && "opacity-0"} transition-all duration-200 `}>
                        <span className="font-semibold text-primary dark:text-blue-500 text-sm sm:text-base">{announcementTitle}</span>
                        <TextFit text={announcementMessage} />
                        {/* <span className="whitespace-break-spaces text-sm sm:text-base">{announcementMessage}</span> */}
                    </div>
                </div>
            </div>
            {
                announcements.length === 0 && showNoAnnouncementsMessage &&
                <span className="text-sm italic">{t("No new announcements")}</span>
            }
            <div className="flex w-full justify-center gap-1 py-2">
                {
                    announcements.map((announcement, index) =>
                        <a key={index} onClick={() => { handleSelectAnnouncement(index) }} 
                            className={`btn btn-xs ${currentAnnouncementIndex === index ? "bg-primary text-white hover:bg-blue-700 border-blue-600 hover:border-blue-700" : "text-gray-800 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"} 
                            ${isEmployee ? 
                                (readAnnouncements.some((item) => item.announcement_id === announcement.id && item.user_id === user.id))
                                ? " bg-primary " : " bg-yellow-500 dark:bg-yellow-600 "
                                : 
                                isMoreThanOneDayPassed(announcement.startDate) ? " bg-primary " : " bg-yellow-500 dark:bg-yellow-600 "}`}>{index + 1}</a>
                    )}
            </div>
        </div>
    )
}



const TextFit = ({ text, maxFontSize = 16, minFontSize = 12, className = "" }) => {
    const containerRef = useRef(null);
    const [fontSize, setFontSize] = useState(maxFontSize);

    useEffect(() => {
        const adjustFontSize = () => {
            const container = containerRef.current;
            if (!container) return;

            let newSize = maxFontSize;
            container.style.fontSize = `${newSize}px`;

            while (container.scrollHeight > container.clientHeight && newSize > minFontSize) {
                newSize -= 1;
                container.style.fontSize = `${newSize}px`;
            }

            setFontSize(newSize);
        };

        adjustFontSize();
        window.addEventListener("resize", adjustFontSize);
        return () => window.removeEventListener("resize", adjustFontSize);
    }, [text, maxFontSize, minFontSize]);

    return (
        <div
            ref={containerRef}
            className={`w-full text-start overflow-hidden whitespace-break-spaces leading-tight ${className}`}
            style={{ fontSize: `${fontSize}px` }}
        >
            {text}
        </div>
    );
};
